import React from "react";
import { observer } from "mobx-react";
import { DesignShowcaseViewStore } from "../../store";
import { DesignShowcaseViewForm } from "./DesignShowcaseViewForm";
import { ErrorDialog } from "../../../../common/controls";
import { DownloadIcon } from "../../../../icons";
import { VisibilityIcon } from "../../../../icons";
import { isOriginalCrudStatus, RowCrudStatus } from "../../../common/types";
import { useRootStore } from "../../../../store/contexts";
import { PDFViewerDialog } from "../../../../common/components/PDFViewerDialog";

export type DesignShowcaseViewProps = {
  designShowcaseViewStore: DesignShowcaseViewStore;
};

export const DesignShowcaseView: React.FC<DesignShowcaseViewProps> = observer(
  ({ designShowcaseViewStore }) => {
    const { langStore } = useRootStore();

    const getViewAction = (
      onDownload: (props: {
        id: string;
        type: string;
        name: string;
        __status__: RowCrudStatus;
      }) => void,
      onViewPdf?: (props: any) => void
    ) => {
      const actions: any[] = [];
      if (onViewPdf) {
        actions.push({
          columnName: "__action_view_pdf",
          onClick: (props: any) =>
            isOriginalCrudStatus(props.__status__) ? onViewPdf(props) : {},
          icon: <VisibilityIcon />,
          label: langStore.viewCommand,
          isActionEnabled: (props: any) =>
            props.name?.toLowerCase().endsWith(".pdf"),
        });
      }
      actions.push({
        columnName: "__action_view",
        onClick: (props: any) =>
          isOriginalCrudStatus(props.__status__) ? onDownload(props) : {},
        icon: <DownloadIcon />,
        label: langStore.downloadCommand,
      });
      return actions;
    };

    return (
      <div>
        <DesignShowcaseViewForm
          designShowcaseViewStore={designShowcaseViewStore}
          code={designShowcaseViewStore.codeValue}
          name={designShowcaseViewStore.nameValue}
          summary={designShowcaseViewStore.summaryValue}
          statusOptions={designShowcaseViewStore.getStatusOptions()}
          status={designShowcaseViewStore.statusValue}
          categoryValue={designShowcaseViewStore.categoryValue}
          relatedCategoriesValues={
            designShowcaseViewStore.relatedCategoriesValues
          }
          appliedStatusesOptions={designShowcaseViewStore.getAppliedStatusesOptions()}
          appliedStatuses={designShowcaseViewStore.appliedStatusesValue}
          lifecycleValue={designShowcaseViewStore.lifecycleValue}
          quantity={designShowcaseViewStore.quantityValue}
          durationInYears={designShowcaseViewStore.durationInYearsValue}
          amount={designShowcaseViewStore.amountValue}
          ratio={designShowcaseViewStore.ratioValue}
          startDate={designShowcaseViewStore.startDateValue}
          expiresAt={designShowcaseViewStore.expiresAtValue}
          registeredAt={designShowcaseViewStore.registeredAtValue}
          active={designShowcaseViewStore.activeValue}
          highlighted={designShowcaseViewStore.highlightedValue}
          phone={designShowcaseViewStore.phoneValue}
          taxId={designShowcaseViewStore.taxIdValue}
          attachmentFiles={designShowcaseViewStore.attachmentFiles}
          anyFileFiles={designShowcaseViewStore.anyFileFiles}
          avatarFiles={designShowcaseViewStore.avatarFiles}
          keywords={designShowcaseViewStore.keywordsValue}
          body={designShowcaseViewStore.bodyValue}
          metadata={designShowcaseViewStore.metadataValue}
          internalNote={designShowcaseViewStore.internalNoteValue}
          serverLabel={designShowcaseViewStore.serverLabelValue}
          linesVisibleColumns={designShowcaseViewStore.linesVisibleColumns}
          linesSetColumnWiths={(x) =>
            designShowcaseViewStore.linesSetColumnWiths(x)
          }
          linesRows={designShowcaseViewStore.linesRowsDyn}
          onPrint={(handlePrint: () => void) =>
            designShowcaseViewStore.printDesignShowcase(handlePrint)
          }
          onCancel={() => designShowcaseViewStore.cancelDesignShowcase()}
          onEdit={
            designShowcaseViewStore.isEditEnabled
              ? () => designShowcaseViewStore.editDesignShowcase()
              : undefined
          }
          viewFormCategory={() => designShowcaseViewStore.viewFormCategory()}
          attachmentViewAction={getViewAction(
            (props) =>
              designShowcaseViewStore.fileDownload(props, "attachment"),
            (props) => designShowcaseViewStore.viewFilePdf(props, "attachment")
          )}
          anyFileViewAction={getViewAction(
            (props) => designShowcaseViewStore.fileDownload(props, "anyFile"),
            (props) => designShowcaseViewStore.viewFilePdf(props, "anyFile")
          )}
          avatarViewAction={getViewAction(
            (props) => designShowcaseViewStore.fileDownload(props, "avatar"),
            (props) => designShowcaseViewStore.viewFilePdf(props, "avatar")
          )}
        />
        <ErrorDialog
          open={designShowcaseViewStore.errorDialogOpened}
          title={designShowcaseViewStore.errorTitle}
          description={designShowcaseViewStore.errorDescription}
          onClose={() => designShowcaseViewStore.closeErrorDialog()}
        />
        <PDFViewerDialog
          open={designShowcaseViewStore.pdfViewerOpen}
          onClose={() => designShowcaseViewStore.closePdfViewer()}
          pdfUrl={designShowcaseViewStore.pdfViewerUrl}
          title={designShowcaseViewStore.pdfViewerTitle}
        />
      </div>
    );
  }
);
